<template>
  <div class="min-h-screen bg-gray-50 py-8">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Header -->
      <div class="mb-8 flex flex-col items-center">
        <Title align="center">Temperature & Motion</Title>
        <div class="mt-2 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm">
          <span class="text-gray-500">Subject</span>
          <span class="px-2 py-0.5 rounded-full bg-gray-100 text-gray-900 font-medium">{{ subjectInfo.subid || 'N/A' }}</span>
          <span class="text-gray-300">•</span>
          <span class="text-gray-500">Dataset</span>
          <span class="px-2 py-0.5 rounded-full bg-gray-100 text-gray-900 font-medium">{{ subjectInfo.dataset_identifier || 'N/A' }}</span>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center items-center py-12">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span class="ml-3 text-gray-600">Loading histogram data...</span>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-md p-4">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
            </svg>
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-red-800">Error loading histogram data</h3>
            <div class="mt-2 text-sm text-red-700">{{ error }}</div>
          </div>
        </div>
      </div>

      <!-- Histogram Content -->
      <div v-else class="bg-white border border-gray-200 rounded-md p-6">
        <!-- Statistical Summary -->
        <div v-if="activeHistogramStats" class="bg-white border border-gray-200 rounded-md p-3 mb-6">
          <div class="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-700">
            <div>
              <span class="text-gray-500 mr-1">Mean:</span>
              <span class="font-semibold">{{ activeHistogramStats.mean.toFixed(2) }}</span>
            </div>
            <div>
              <span class="text-gray-500 mr-1">SD:</span>
              <span class="font-semibold">{{ activeHistogramStats.sd.toFixed(2) }}</span>
            </div>
            <div>
              <span class="text-gray-500 mr-1">Min:</span>
              <span class="font-semibold">{{ activeHistogramStats.min.toFixed(2) }}</span>
            </div>
            <div>
              <span class="text-gray-500 mr-1">Max:</span>
              <span class="font-semibold">{{ activeHistogramStats.max.toFixed(2) }}</span>
            </div>
          </div>
        </div>

        <!-- Histogram Toggle Buttons -->
        <div class="inline-flex rounded-lg bg-gray-100 p-1 mb-4" role="tablist" aria-label="Histogram Toggle">
          <button 
            @click="selectHistogram('Temperature_C')" 
            :aria-pressed="histogram.active === 'Temperature_C'" 
            :class="histogram.active === 'Temperature_C' ? 'bg-white text-blue-600 shadow' : 'text-gray-700 hover:text-gray-900'" 
            class="px-4 py-1.5 text-sm font-medium rounded-md focus:outline-none" 
            role="tab"
          >
            Temperature
          </button>
          <button 
            @click="selectHistogram('Motion')" 
            :aria-pressed="histogram.active === 'Motion'" 
            :class="histogram.active === 'Motion' ? 'bg-white text-blue-600 shadow' : 'text-gray-700 hover:text-gray-900'" 
            class="ml-1 px-4 py-1.5 text-sm font-medium rounded-md focus:outline-none" 
            role="tab"
          >
            Motion
          </button>
        </div>

        <!-- Error Message -->
        <div v-if="histogram.error" class="mt-2 text-sm text-red-600 mb-4">{{ histogram.error }}</div>

        <!-- Histogram Chart -->
        <div class="bg-gray-50 rounded p-4 h-96">
          <div v-if="histogram.loading" class="flex items-center text-sm text-gray-500">
            <svg class="mr-2 h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 004 4H4z"></path>
            </svg>
            Loading histogram…
          </div>
          <BaseChart 
            v-else-if="histogram.chartData && histogram.chartOptions" 
            ref="histChart" 
            :chart-type="'bar'" 
            :chart-data="histogram.chartData" 
            :chart-options="histogram.chartOptions" 
            :chart-plugins="histogram.chartPlugins" 
            :width="800" 
            :height="360" 
            :auto-update="true" 
          />
          <div v-else class="text-sm text-gray-500">No histogram data to display.</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { buildApiUrl, API_ENDPOINTS } from '../../config/api.js'
import BaseChart from '../../components/BaseChart.vue'
import { buildHistogramChart } from '../../components/histogram.js'
import Title from '../../components/Title.vue'

export default {
  name: 'TempMotionResults',
  inject: ['setBackButton', 'hideBackButton'],
  components: {
    BaseChart,
    Title
  },
  data() {
    return {
      loading: true,
      error: null,
      sessionId: null,
      subjectInfo: {},
      histogram: {
        loading: false,
        generated: false,
        active: 'Temperature_C',
        temperature: null,
        motion: null,
        chartData: null,
        chartOptions: null,
        chartPlugins: null,
        error: null,
        activeStats: null
      }
    }
  },
  computed: {
    activeHistogramStats() {
      return this.histogram?.activeStats || null
    }
  },
  methods: {
    computeHistogramStats(payload) {
      try {
        const edges = payload?.binEdges || []
        const counts = payload?.counts || payload?.density || []
        if (!edges.length || !counts.length) return null
        // approximate value at bin center
        const centers = []
        for (let i = 0; i < edges.length - 1; i++) {
          centers.push((edges[i] + edges[i + 1]) / 2)
        }
        const weights = counts
        const totalWeight = weights.reduce((a, b) => a + b, 0)
        if (totalWeight === 0) return null
        const mean = centers.reduce((sum, center, i) => sum + center * weights[i], 0) / totalWeight
        const variance = centers.reduce((sum, center, i) => sum + Math.pow(center - mean, 2) * weights[i], 0) / totalWeight
        const sd = Math.sqrt(variance)
        const min = Math.min(...centers)
        const max = Math.max(...centers)
        return { mean, sd, min, max }
      } catch (e) {
        console.warn('Failed to compute histogram stats:', e)
        return null
      }
    },

    async generateHistogram() {
      // Get session ID and s3_key from storage
      let s3Key = null
      try {
        const processingResults = sessionStorage.getItem('processingResults')
        if (processingResults) {
          const results = JSON.parse(processingResults)
          this.sessionId = results.session_id || null
          s3Key = results.s3_key || null
        }
      } catch (error) {
        console.warn('Failed to load session ID from storage:', error)
      }

      if (!this.sessionId && !s3Key) {
        this.histogram.error = 'No active session or prior results available. Please process data first.'
        return
      }

      this.histogram.loading = true
      this.histogram.error = null
      try {
        // Build request payload - use s3_key if available (for prior results), otherwise use session_id
        const basePayload = {
          field: 'Temperature_C',
          bins: { width: 1 }
        }
        if (s3Key) {
          basePayload.s3_key = s3Key
          console.log('📦 Using s3_key for histogram (prior result):', s3Key)
        } else {
          basePayload.session_id = this.sessionId
          console.log('📦 Using session_id for histogram (active session):', this.sessionId)
        }

        const motionPayload = {
          ...basePayload,
          field: 'Motion',
          bins: { width: 0.05 }
        }

        const [tempResp, motionResp] = await Promise.all([
          fetch(buildApiUrl(API_ENDPOINTS.GET_HISTOGRAM), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(basePayload)
          }),
          fetch(buildApiUrl(API_ENDPOINTS.GET_HISTOGRAM), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(motionPayload)
          })
        ])

        const tempJson = await tempResp.json()
        const motionJson = await motionResp.json()
        if (!tempResp.ok || !tempJson.success) throw new Error(tempJson.message || 'Failed to compute Temperature histogram')
        if (!motionResp.ok || !motionJson.success) throw new Error(motionJson.message || 'Failed to compute Motion histogram')

        this.histogram.temperature = tempJson.data
        this.histogram.motion = motionJson.data
        this.histogram.generated = true

        // Default to Temperature
        this.selectHistogram('Temperature_C')
      } catch (e) {
        this.histogram.error = e?.message || 'Error generating histograms'
      } finally {
        this.histogram.loading = false
      }
    },

    selectHistogram(field) {
      this.histogram.active = field
      const payload = field === 'Motion' ? this.histogram.motion : this.histogram.temperature
      if (!payload) {
        this.histogram.chartData = null
        this.histogram.chartOptions = null
        return
      }
      const { data, options, plugins } = buildHistogramChart(payload.binEdges, payload.counts ?? payload.density, {
        label: field === 'Motion' ? 'Motion' : 'Temperature',
        color: field === 'Motion' ? 'rgba(234, 88, 12, 0.6)' : 'rgba(59, 130, 246, 0.6)',
        borderColor: field === 'Motion' ? 'rgba(194, 65, 12, 1)' : 'rgba(37, 99, 235, 1)',
        density: !!payload.density,
        xLabel: field,
        yLabel: payload.density ? 'Density' : 'Count',
        showLegend: false
      })
      this.histogram.chartData = data
      this.histogram.chartOptions = options
      this.histogram.chartPlugins = plugins
      // Compute summary stats from histogram bins
      this.histogram.activeStats = this.computeHistogramStats(payload)
      this.$nextTick(() => {
        const chartComp = this.$refs.histChart
        if (chartComp && chartComp.updateChart) {
          chartComp.updateChart(null, null)
        }
      })
    },

    loadHistogramData() {
      this.loading = true
      this.error = null
      
      try {
        // Check if we have processing results
        const storedResults = sessionStorage.getItem('processingResults')
        if (!storedResults) {
          this.error = 'No processing results found. Please run data processing first.'
          return
        }

        // Load subject info from processing results
        const results = JSON.parse(storedResults)
        this.subjectInfo = {
          subid: results.subid,
          dataset_identifier: results.dataset_identifier
        }

        // Generate histogram data
        this.generateHistogram()
      } catch (error) {
        console.error('Error loading histogram data:', error)
        this.error = 'Failed to load histogram data: ' + error.message
      } finally {
        this.loading = false
      }
    }
  },
  mounted() {
    // Set up back button to return to dashboard
    this.setBackButton({
      text: 'Return to Dashboard',
      route: '/subject-dashboard'
    })
    
    this.loadHistogramData()
  }
}
</script>
